import React, { useState, createContext, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

const Context = createContext();

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @component Collapsible
 * @props (see propTypes)
 * @example:
 *     <Collapsible {...}>
 *         <Collapsible.Heading>...</Collapsible.Heading>
 *         <Collapsible.Content>...</Collapsible.Content>
 *     </Collapsible>
 *
 * This module implements the ARIA guidelines available at the following URL: https://www.w3.org/TR/wai-aria-practices-1.1/examples/accordion/accordion.html
 */
const Collapsible = props => {
    const [ ids ] = useState({
        module: props.id,
        heading: props.id + '-heading',
        content: props.id + '-content'
    });
    const [ isOpened, setIsOpened ] = useState(props.isOpened ?? false);
    const [ isClosable ] = useState(props.isClosable ?? true);
    const [ hasToggle ] = useState(props.hasToggle ?? true);
    const context = { ids, isOpened, setIsOpened, isClosable, hasToggle };

    useEffect(() => {
        props.getState && props.getState({ isOpened });
    }, [ isOpened ]);


    useEffect(() => {
        if (typeof props.open === 'boolean') {
            setIsOpened(props.open);
        }
    }, [props.open]);
    return (
        <Context.Provider value={context}>
            <div
                className={props.className}
                id={ids.module}>
                {props.children}
            </div>
        </Context.Provider>
    );
};

/**
 * @component Heading
 * @props (see propTypes)
 *
 * Represents the heading container within the Collapsible.
 */
const Heading = props => {
    const { ids, isOpened, setIsOpened, isClosable } = useContext(Context);

    return (
        <button
            aria-controls={ids.content}
            aria-disabled={isOpened && !isClosable ? 'true' : 'false'}
            className={props.className}
            data-expanded={isOpened ? 'true' : 'false'}
            disabled={isOpened && !isClosable}
            id={ids.heading}
            onClick={() => (!isOpened || isClosable) && setIsOpened(!isOpened)}
            type='button'>
            {props.children}
        </button>
    );
};

/**
 * @component Content
 * @props (see propTypes)
 *
 * Represents the content container within the Collapsible.
 */
const Content = props => {
    const { ids, isOpened, hasToggle } = useContext(Context);

    return (
        <div
            aria-hidden={!isOpened}
            aria-labelledby={ids.heading}
            className={props.className}
            hidden={!isOpened && hasToggle}
            id={ids.content}>
            {props.children}
        </div>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

Collapsible.propTypes = {
    getState: PropTypes.func,
    id: PropTypes.string.isRequired,
    isOpened: PropTypes.bool,
    isClosable: PropTypes.bool,
    hasToggle: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
    ])
};

Heading.propTypes = {
    className: PropTypes.string,
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
    ])
};

Content.propTypes = {
    className: PropTypes.string,
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
    ])
};

const CollapsibleHeading = Heading;
const CollapsibleContent = Content;
const CollapsibleContext = Context;
const CollapsibleContextConsumer = Context.Consumer;

export {
    Collapsible,
    CollapsibleHeading,
    CollapsibleContent,
    CollapsibleContext,
    CollapsibleContextConsumer
};
